import { Gauge } from "lucide-react";
import Money from "@/components/Money";

/**
 * Customer-facing mileage policy block on the car detail page. Shows nothing when the
 * vendor hasn't specified a policy (so we don't pad the page with empty sections).
 * Three render branches:
 *   - "unlimited": emerald perk badge + reassurance copy.
 *   - "limited": amber chip with the daily cap + extra-km rate so the renter can model
 *      a road-trip cost before booking.
 *   - anything else: null.
 *
 * Mileage is INFORMATIONAL at booking time per product spec — the vendor settles overage
 * via the existing deposit-withhold flow at trip-end.
 */
export default function CarMileagePolicy({ policy, limit, rate }) {
  if (!policy) return null;
  return (
    <div className="py-8 border-b border-border" data-testid="car-mileage-policy">
      <h3 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center">
          <Gauge className="h-4 w-4 text-violet-600" />
        </span>
        Mileage policy
      </h3>
      {policy === "unlimited" ? (
        <>
          <div className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 mb-3" data-testid="mileage-unlimited">
            Unlimited mileage
          </div>
          <p className="text-neutral-700 leading-relaxed text-sm">
            Drive as far as you like — there's no daily km cap on this rental. Cross-border travel may still need the vendor's written permission.
          </p>
        </>
      ) : (
        <>
          <div className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 mb-3" data-testid="mileage-limited">
            {limit ? `${Math.round(limit)} km / day included` : "Daily km cap"}
          </div>
          <p className="text-neutral-700 leading-relaxed text-sm">
            Extra km beyond the daily allowance{rate ? <> are billed at <strong><Money amount={rate} />/km</strong></> : ""}, settled by the vendor at trip-end from your refundable deposit.
            {limit && rate ? <> So a 7-day rental gives you {Math.round(limit) * 7} km free; every km over that is <Money amount={rate} />.</> : ""}
          </p>
        </>
      )}
    </div>
  );
}
